import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, ButtonGroup, Chip, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
    AccountTreeOutlined,
    AutoAwesomeMosaicOutlined,
    BadgeOutlined,
    BlockOutlined,
    CheckCircleOutline,
    DownloadingOutlined,
    DownloadOutlined,
    EditOutlined,
    FileDownloadOutlined,
    PersonAdd,
    WorkspacePremiumOutlined,
} from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import appRoutes from '../../../routes/routeConfig';
import Filters from '../../../components/Filters';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import { currencyFormat, getPrivilegeStatus, getStatusColor, testUsers } from '../../../util';
import { getAllInstitutions, getAllInstitutionsMemos } from '../../../redux/api/institution';
import { useSnackbar } from 'notistack';
import { getDocument } from '../../../redux/api/workflows';

const sections = ['Unassigned Breached Cases', 'FI Unassigned Breached Cases', 'Public Unassigned Breached Cases', 'CPD Unassigned Breached Cases'];

const Institutions = () => {
    const [processing, setProcessing] = useState(false);
    const [fetching, setFetching] = useState(null);
    const [performingAction, setPerformingAction] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [selectedInstitution, setSelectedInstitution] = useState(null);
    const [section, setSection] = useState(0);
    const [searchResults, setSearchResults] = useState([]);

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.institutionId) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Quick Action',
            width: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        {getPrivilegeStatus(['getInstitutionById', 'getInstitutionByInstCode']) && (
                            <IconButton
                                size="small"
                                color="primary"
                                variant="outlined"
                                title="Edit"
                                onClick={() => {
                                    history.push(`/internal/institution/inst/${params?.row?.institutionId}`);
                                }}
                            >
                                <EditOutlined fontSize="small" />
                            </IconButton>
                        )}
                        {getPrivilegeStatus('getAllInstitutionsMemos') && (
                            <IconButton
                                size="small"
                                color="info"
                                variant="outlined"
                                title="Institution Memos"
                                onClick={() => {
                                    openMemo(params?.row);
                                }}
                            >
                                {Boolean(fetching == params?.row?.institutionId) ? <CircularProgress color="inherit" size={15} /> : <AutoAwesomeMosaicOutlined fontSize="small" />}
                            </IconButton>
                        )}
                    </div>
                );
            },
        },
        { field: 'code', headerName: 'Institution Code', width: 200 },
        { field: 'name', headerName: 'Name', width: 300 },
        { field: 'email', headerName: 'Contact Email', width: 350 },
        {
            field: 'createdDate',
            headerName: 'Creation Date',
            width: 200,
            valueGetter: (params) => (params?.row?.createdOn ? moment(params?.row?.createdOn).format('DD/MM/YY hh:mm') : ''),
        },
        { field: 'createdBy', headerName: 'Creation By', width: 200 },
    ];

    const openMemo = async (institution) => {
        try {
            setFetching(institution?.institutionId);
            const resp = await getAllInstitutionsMemos(institution?.institutionId);
            setSelectedInstitution(resp?.resultObject?.[0]);
            setActionModal(true);
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setFetching(null);
        }
    };

    const performAction = async () => {
        const status = !Boolean(actionModal?.isActive);
        try {
            setPerformingAction(true);
            // await deactivateUser({
            //     id: actionModal?.id,
            //     isActive: status
            // });
            setActionModal(null);
            enqueueSnackbar(`User ${!status && 'de'}activated successfully`);
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setPerformingAction(false);
        }
    };

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getAllInstitutions();
            setRecords(res?.resultObject || []);
            setSearchResults(res?.resultObject || []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleSearch = (values) => {
        setSearchResults(
            records.filter((record) => {
                let valid = true;
                if (values.name) {
                    valid = valid && record.name.toLowerCase().includes(values.name.toLowerCase());
                }

                if (values.code) {
                    valid = valid && record.code.toLowerCase().includes(values.code.toLowerCase());
                }

                return valid;
            })
        );
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="All Institutions"
            tagline="Manage all institutions"
            processing={processing}
            buttonComponent={
                <>
                    <Link to={appRoutes.internal.institutions.create}>
                        <Button startIcon={<PersonAdd />} style={{ margin: '0 .5rem' }} variant="contained">
                            Create Institution
                        </Button>
                    </Link>
                </>
            }
        >
            <Filters
                searchFields={[
                    { id: 'code', label: 'Code' },
                    { id: 'name', label: 'Name' },
                ]}
                searchFunction={handleSearch}
                useDefault={false}
                showClearBtn={false}
            />

            <Box>
                <div className="paper" style={{ height: 550, width: '100%', textAlign: 'center' }}>
                    <DataGrid
                        className={'center-first-column'}
                        rows={searchResults}
                        getRowId={(row) => row.institutionId}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Dialog open={Boolean(actionModal)} maxWidth="lg" PaperProps={{ style: { minWidth: '750px' } }}>
                <DialogContent>
                    <h4>Institution Memos: {selectedInstitution?.institutionName}</h4>
                    <ButtonGroup variant="outlined" className="mb-1 mt-05" fullWidth>
                        <Button variant={section === 0 ? 'contained' : 'outlined'} onClick={() => setSection(0)}>
                            Regulatory Directives ({selectedInstitution?.regulatoryDirectives?.length || 0})
                        </Button>

                        <Button variant={section === 1 ? 'contained' : 'outlined'} onClick={() => setSection(1)}>
                            Sanctions ({selectedInstitution?.sanctions?.length || 0})
                        </Button>

                        <Button variant={section === 2 ? 'contained' : 'outlined'} onClick={() => setSection(2)}>
                            Technical Sessions ({selectedInstitution?.technicalSessions?.length || 0})
                        </Button>

                        <Button variant={section === 3 ? 'contained' : 'outlined'} onClick={() => setSection(3)}>
                            Warnings ({selectedInstitution?.warnings?.length || 0})
                        </Button>
                    </ButtonGroup>

                    {section == 0 && (
                        <List>
                            {selectedInstitution?.regulatoryDirectives?.map((directives, key) => (
                                <ListItem key={key} divider className="pl-0 pr-0">
                                    <span className="p-3">{key + 1}.</span>
                                    <ListItemText primary={directives.title} secondary={directives?.description || ''} />

                                    <ListItemIcon
                                        style={{
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                        onClick={async () => {
                                            await getDocument(directives?.documentId, directives?.documentFileName || directives?.title);
                                        }}
                                    >
                                        <FileDownloadOutlined />
                                    </ListItemIcon>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {section == 1 && (
                        <List>
                            {selectedInstitution?.sanctions?.map((sanction, key) => (
                                <ListItem key={key} divider className="pl-0 pr-0">
                                    <span className="p-3">{key + 1}.</span>
                                    <ListItemText primary={sanction.title} secondary={sanction?.description || ''} />
                                    <ListItemText primary={`NGN ${currencyFormat.format(sanction?.amount || 0)}`} secondary={`Paid: ${sanction?.isPaid ? 'True' : 'False'}`} />

                                    <ListItemIcon
                                        style={{
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                        onClick={async () => {
                                            await getDocument(sanction?.documentId, sanction?.documentFileName || sanction?.title);
                                        }}
                                    >
                                        <FileDownloadOutlined />
                                    </ListItemIcon>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {section == 2 && (
                        <List>
                            {selectedInstitution?.technicalSessions?.map((session, key) => (
                                <ListItem key={key} divider className="pl-0 pr-0">
                                    <span className="p-3">{key + 1}.</span>
                                    <ListItemText primary={session.title} secondary={session?.description || ''} />
                                    <ListItemText primary={`Start Date: ${moment(session.startDate).format('DD-MM-YYYY')}`} secondary={`End Date: ${moment(session.endDate).format('DD-MM-YYYY')}`} />

                                    <ListItemIcon
                                        style={{
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                        onClick={async () => {
                                            await getDocument(session?.documentId, session?.documentFileName || session?.title);
                                        }}
                                    >
                                        <FileDownloadOutlined />
                                    </ListItemIcon>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {section == 3 && (
                        <List>
                            {selectedInstitution?.warnings?.map((warning, key) => (
                                <ListItem key={key} divider className="pl-0 pr-0">
                                    <span className="p-3">{key + 1}.</span>
                                    <ListItemText primary={warning.title} secondary={warning?.description || ''} />
                                    <ListItemIcon
                                        style={{
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                        onClick={async () => {
                                            await getDocument(warning?.documentId, warning?.documentFileName || warning?.title);
                                        }}
                                    >
                                        <FileDownloadOutlined />
                                    </ListItemIcon>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions className="pb-1 pr-1">
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            setActionModal(null);
                            setSection(0);
                        }}
                        disabled={performingAction}
                    >
                        Cancel
                    </Button>
                    {/* <Button variant='contained' color="primary" onClick={performAction} disabled={performingAction}>
                        {Boolean(performingAction) ? <CircularProgress color="inherit" size={15} /> : Boolean(actionModal?.isActive) ? 'Deactivate' : 'Activate'}
                    </Button> */}
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default Institutions;
